import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { logger } from '@gem/modules/logger';
import { ICommonPopupsProps } from '@gem/popups';

import {
  getGroupAdvertisementSettings,
  setGroupAdvertisementSettings,
} from 'APP/model/group/groupModel';
import { GroupType } from 'APP/model/group/groupModel.types';
import bus from 'APP/packages/bus';
import { RouterLink, RouterState } from 'APP/router/constants';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';

export interface IMonetizationPopupPresenterParams {
  groupId: string;
}

interface IMonetizationPopupPresenter {
  isChecked: boolean;
  isLoading: boolean;
  group: Group | null;
  isShow: boolean;
  onClose(): void;
  onClick(): void;
  init(): Promise<void>;
  toggle(): void;
  onSave(): void;
}

export function useMonetizationPopupPresenter({
  params,
  popupInstance,
}: ICommonPopupsProps<IMonetizationPopupPresenterParams>): IMonetizationPopupPresenter {
  const navigateTo = useNavigateTo();
  const { groupId } = params!;

  const presenter = useLocalStore<IMonetizationPopupPresenter>(() => ({
    isChecked: false,
    isLoading: false,

    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get isShow(): boolean {
      return !!presenter.group?.isOwner && presenter.group?.type === GroupType.ChannelOpen;
    },

    onClose(): void {
      popupInstance.close();
    },

    onClick(): void {
      navigateTo({
        to: RouterLink.Market,
        state: {
          [RouterState.url]: Entities.marketplace.baseAdsUrl,
        },
      });
    },

    async init(): Promise<void> {
      if (!presenter.group?.id) {
        return;
      }

      try {
        presenter.isLoading = true;
        const { adsEnabled } = await getGroupAdvertisementSettings({
          groupId: presenter.group.id,
        });
        this.isChecked = adsEnabled;
      } catch (e) {
        logger.get('MonetizationPopup').error('init', e.data);
      }
      presenter.isLoading = false;
    },

    toggle(): void {
      presenter.isChecked = !presenter.isChecked;
    },

    async onSave(): Promise<void> {
      presenter.onClose();

      if (!presenter.group?.id) {
        return;
      }
      try {
        await setGroupAdvertisementSettings({
          groupId: presenter.group.id,
          adsEnabled: presenter.isChecked,
        });
      } catch (e) {
        return;
      }
    },
  }));

  useEffect(() => {
    if (!presenter.isShow) {
      presenter.onClose();
    } else {
      presenter.init();
    }
  }, []);

  useEffect(() => {
    return bus.group.subscribeAdsConfigChanges(
      presenter.group?.id,
      ({ adsEnabled }: { adsEnabled: boolean }) => {
        presenter.isChecked = adsEnabled;
      }
    );
  }, [presenter.group?.id]);

  return presenter;
}
